import React, { useState, useEffect } from 'react';
import './ClientsDashboard.css';
import Confirm from './Confirm';
import ImageUploadZone from './ImageUploadZone';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableClientItem({ client, idx }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: client._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="sortable-item"
      onTouchStart={(e) => e.preventDefault()}
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        attributes.onMouseDown?.(e);
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '16px',
        marginBottom: '12px',
        backgroundColor: isDragging ? 'rgba(244,241,234,0.1)' : 'rgba(244,241,234,0.02)',
        border: '1px solid rgba(244,241,234,0.08)',
        borderRadius: '2px',
        cursor: 'grab',
      }}>
        <div style={{
          fontSize: '13px',
          color: 'rgba(244,241,234,0.4)',
          fontFamily: 'var(--mono)',
          minWidth: '24px'
        }}>
          {idx + 1}
        </div>
        {client.logo && (
          <img
            src={client.logo}
            alt={client.name}
            style={{
              height: '32px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>{client.name}</div>
          <div style={{ fontSize: '13px', color: 'rgba(244,241,234,0.6)' }}>{client.industry}</div>
        </div>
        <div style={{ fontSize: '11px', color: 'var(--graphite-2)' }}>
          {client.visibility.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

export default function ClientsDashboard() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('rank');
  const [displayCount, setDisplayCount] = useState(6);
  const [editingId, setEditingId] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [rankMode, setRankMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    logo: '',
    visibility: 'public',
    rank: 0
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      distance: 8,
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchClients();
    fetchDisplayCount();
  }, []);

  useEffect(() => {
    saveDisplayCount();
  }, [displayCount]);

  const fetchDisplayCount = async () => {
    try {
      const response = await fetch('/api/settings/clients-display-count');
      const data = await response.json();
      if (data.value) setDisplayCount(data.value);
    } catch (err) {
      console.error('Error fetching display count:', err);
    }
  };

  const saveDisplayCount = async () => {
    try {
      await fetch('/api/settings/clients-display-count', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: displayCount })
      });
    } catch (err) {
      console.error('Error saving display count:', err);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch('/api/clients');
      const data = await response.json();
      setClients(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setLoading(false);
    }
  };

  const sortedClients = [...clients].sort((a, b) => {
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    if (sortBy === 'rank') return a.rank - b.rank;
    if (sortBy === 'industry') return a.industry.localeCompare(b.industry);
    return 0;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const response = await fetch(`/api/clients/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        if (!response.ok) throw new Error('Update failed');
        setClients(clients.map(c => c._id === editingId ? { ...c, ...formData } : c));
        setEditingId(null);
      } else {
        const response = await fetch('/api/clients', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        if (!response.ok) throw new Error('Create failed');
        const newClient = await response.json();
        setClients([...clients, newClient]);
        setShowNewForm(false);
      }
      setFormData({ name: '', industry: '', logo: '', visibility: 'public', rank: 0 });
    } catch (err) {
      console.error('Error:', err);
      alert('Error saving client');
    }
  };

  const handleEdit = (client) => {
    setEditingId(client._id);
    setFormData({
      name: client.name,
      industry: client.industry,
      logo: client.logo || '',
      visibility: client.visibility,
      rank: client.rank
    });
    setShowNewForm(false);
  };

  const handleDelete = async (id) => {
    const client = clients.find(c => c._id === id);
    setConfirm({
      title: `Delete "${client?.name}"?`,
      onConfirm: async () => {
        try {
          const response = await fetch(`/api/clients/${id}`, { method: 'DELETE' });
          if (!response.ok) throw new Error('Delete failed');
          setClients(clients.filter(c => c._id !== id));
          setConfirm(null);
        } catch (err) {
          console.error('Error:', err);
          alert('Error deleting client');
        }
      },
      onCancel: () => setConfirm(null)
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setShowNewForm(false);
    setFormData({ name: '', industry: '', logo: '', visibility: 'public', rank: 0 });
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = clients.findIndex(c => c._id === active.id);
    const newIndex = clients.findIndex(c => c._id === over.id);

    const reordered = arrayMove(clients, oldIndex, newIndex);
    setClients(reordered);

    try {
      const updatePromises = reordered.map((client, idx) => {
        const clientData = { ...client };
        delete clientData._id;
        return fetch(`/api/clients/${client._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...clientData, rank: idx })
        });
      });

      await Promise.all(updatePromises);
    } catch (err) {
      console.error('Error updating order:', err);
      fetchClients();
    }
  };

  if (loading) return <div className="dashboard">Loading clients...</div>;

  return (
    <div className="dashboard clients-dashboard">
      <div className="dashboard-header">
        <h1>Trusted Partners</h1>
        <div className="dashboard-controls">
          {!rankMode && (
            <>
              <div className="control-group">
                <label>Display Count:</label>
                <input
                  type="number"
                  min="1"
                  value={displayCount}
                  onChange={(e) => setDisplayCount(Math.max(1, parseInt(e.target.value) || 1))}
                  className="input-number"
                />
              </div>
              <div className="control-group">
                <label>Sort by:</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="select-control">
                  <option value="name">Name</option>
                  <option value="rank">Rank</option>
                  <option value="industry">Industry</option>
                </select>
              </div>
            </>
          )}
          <button
            className={`btn-primary ${rankMode ? 'is-active' : ''}`}
            onClick={() => setRankMode(!rankMode)}
            style={{ marginRight: '12px', opacity: rankMode ? 1 : 0.6 }}
          >
            {rankMode ? '✓ Done ranking' : '↕ Rank'}
          </button>
          {!rankMode && (
            <button className="btn-primary" onClick={() => setShowNewForm(!showNewForm)}>
              {showNewForm ? 'Cancel' : '+ Add Partner'}
            </button>
          )}
        </div>
      </div>

      {rankMode ? (
        <div style={{ marginTop: '32px' }}>
          <p style={{ fontSize: '13px', color: 'rgba(244,241,234,0.6)', marginBottom: '24px' }}>Drag partners to set their display order.</p>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={clients.map(c => c._id)}
              strategy={verticalListSortingStrategy}
            >
              <div style={{ padding: '16px', borderRadius: '4px' }}>
                {clients.map((client, idx) => (
                  <SortableClientItem key={client._id} client={client} idx={idx} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      ) : (
        <>
          {(showNewForm || editingId) && (
            <div className="form-container">
              <h2>{editingId ? 'Edit Partner' : 'New Partner'}</h2>
              <form onSubmit={handleSubmit} className="partner-form">
            <div className="form-row">
              <label>
                Name
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Client name"
                  required
                />
              </label>
              <label>
                Industry
                <input
                  type="text"
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  placeholder="Industry"
                  required
                />
              </label>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(244,241,234,0.6)' }}>Logo</h3>
              {formData.logo && (
                <div style={{ marginBottom: '16px' }}>
                  <img
                    src={formData.logo}
                    alt="Logo"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '120px',
                      objectFit: 'contain',
                      marginBottom: '12px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, logo: '' })}
                    className="btn-ghost"
                    style={{ color: '#ff6b6b' }}
                  >
                    Remove logo
                  </button>
                </div>
              )}
              <ImageUploadZone
                onImageAdded={(imageUrl) => setFormData({ ...formData, logo: imageUrl })}
                type="logo"
              />
            </div>

            <div className="form-row">
              <label>
                Rank
                <input
                  type="number"
                  value={formData.rank}
                  onChange={(e) => setFormData({ ...formData, rank: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
              </label>
            </div>
            <div className="form-row">
              <label>
                Visibility
                <select
                  value={formData.visibility}
                  onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
                >
                  <option value="public">Public</option>
                  <option value="nda">NDA</option>
                  <option value="private">Private</option>
                </select>
              </label>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-success">{editingId ? 'Update' : 'Create'}</button>
              <button type="button" className="btn-secondary" onClick={handleCancel}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="clients-grid">
        <div className="clients-header">
          <div className="col-logo">Logo</div>
          <div className="col-name">Name</div>
          <div className="col-industry">Industry</div>
          <div className="col-rank">Rank</div>
          <div className="col-visibility">Visibility</div>
          <div className="col-actions">Actions</div>
        </div>
        {sortedClients.map((client) => (
          <div key={client._id} className="client-row">
            <div className="col-logo">
              {client.logo ? (
                <img src={client.logo} alt={client.name} className="logo-img" />
              ) : (
                <div className="logo-placeholder">{client.name.charAt(0)}</div>
              )}
            </div>
            <div className="col-name">{client.name}</div>
            <div className="col-industry">{client.industry}</div>
            <div className="col-rank">{client.rank}</div>
            <div className="col-visibility">
              <span className={`badge badge-${client.visibility}`}>{client.visibility}</span>
            </div>
            <div className="col-actions">
              <button className="btn-edit" onClick={() => handleEdit(client)}>Edit</button>
              <button className="btn-delete" onClick={() => handleDelete(client._id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>

          <div className="preview-section">
            <h2>Frontend Preview (showing first {displayCount})</h2>
            <div className="preview-grid">
              {[...clients].sort((a, b) => a.rank - b.rank).slice(0, displayCount).map((client) => (
                <div key={client._id} className="preview-cell">
                  {client.logo ? (
                    <img src={client.logo} alt={client.name} className="preview-logo" />
                  ) : (
                    <div className="preview-placeholder">{client.name}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {confirm && (
        <Confirm
          title={confirm.title}
          body=""
          onConfirm={confirm.onConfirm}
          onCancel={confirm.onCancel}
          confirmLabel="Delete"
          danger
        />
      )}
    </div>
  );
}
